import { useGetSyncingAccountsQuery } from '@cointracker/graphql-types';
import { URLS } from '@cointracker/ui';
import { CostBasisContext } from 'components/CostBasisProvider';
import { useContext, useState } from 'react';
import dayjs from 'src/app/utils/dayjsConfig';
import {
  isAnyAssetSyncing,
  isExchangeSyncing,
  isWalletSyncing,
} from 'src/pages/Rebrand/Wallets/WalletsContainer/utils';
import { NavigationType } from 'src/pages/Rebrand/Wallets/constants';
import { useGate } from 'statsig-react';
import { getIconFromCurrency } from '../../../../../pages/Rebrand/TransactionForm/WalletSelect/hooks/useGetWallets';

export enum StatusReadyState {
  Ready,
  Syncing,
  Calculating,
  Failed,
  Loading,
  EmptyData,
  Paused,
}

export interface SyncAssetItem {
  title: string;
  subtitle: string;
  value: number;
  isSyncing: boolean;
  url: string;
  imgUrl: string;
}

export const useGetStatus = () => {
  const {
    isCostBasisRunning,
    isUndergoingAllocation,
    hasError,
    lastSyncedAt,
    costBasisPaused,
  } = useContext(CostBasisContext);
  const [isStatusSheetOpen, setIsStatusSheetOpen] = useState(false);
  const { value: shouldUsePaginatedWallets } = useGate('wallets_pagination');

  const fiveMinutesAgo = dayjs().subtract(5, 'minutes');
  const hasMoreThan5MinutesPassedSinceLastSync =
    dayjs(lastSyncedAt).isBefore(fiveMinutesAgo);

  const shouldAllowUserToResync =
    isCostBasisRunning && hasMoreThan5MinutesPassedSinceLastSync;

  const { data, loading } = useGetSyncingAccountsQuery({
    // TODO: we need to introduce a different query or figure out how to paginate this to
    //       avoid the app crashing. Until then, not getting syncing wallets should be okay enough since it's not
    //       mission critical.
    skip: shouldUsePaginatedWallets,
  });
  const hasSyncingAssets = isAnyAssetSyncing(data, false);

  const syncingAssets: SyncAssetItem[] = [];

  data?.exchanges?.forEach((exchange) => {
    const syncAsset = {
      title: exchange.name,
      subtitle: `${exchange.assetsCount} assets`,
      value: exchange.value,
      isSyncing: isExchangeSyncing(exchange),
      url: `${URLS.WALLETS_PAGE}/${NavigationType.EXCHANGE}/${exchange.id}`,
      imgUrl: exchange.imageUrl,
    };
    if (syncAsset.isSyncing) {
      syncingAssets.push(syncAsset);
    }
  });

  data?.localWallets?.forEach((wallet) => {
    const syncAsset = {
      title: wallet.name,
      subtitle: wallet.address,
      value: wallet.value,
      isSyncing: isWalletSyncing(wallet),
      url: `${URLS.WALLETS_PAGE}/${NavigationType.WALLET}/${wallet.id}`,
      imgUrl: wallet.imageUrl ?? getIconFromCurrency(wallet?.currency),
    };
    if (syncAsset.isSyncing) {
      syncingAssets.push(syncAsset);
    }
  });

  let status = StatusReadyState.Ready;
  if (costBasisPaused) {
    status = StatusReadyState.Paused;
  }
  if (isCostBasisRunning || isUndergoingAllocation) {
    status = StatusReadyState.Calculating;
  }
  if (hasSyncingAssets) {
    status = StatusReadyState.Syncing;
  }
  if (hasError) {
    status = StatusReadyState.Failed;
  }
  if (loading) {
    status = StatusReadyState.Loading;
  }
  if (
    status === StatusReadyState.Ready &&
    data?.exchanges?.length === 0 &&
    data?.localWallets?.length === 0
  ) {
    status = StatusReadyState.EmptyData;
  }

  return {
    status,
    syncingAssets,
    shouldAllowUserToResync,
    isStatusSheetOpen,
    setIsStatusSheetOpen,
  };
};
